import React, { FC, useEffect, useState } from 'react';
import {
  Alert,
  Col,
  Collapse,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Spin,
} from 'antd';

import { useTranslate } from '@/translations';
import { TranslationKey } from '@/translations/types';
import {
  useInspectionsMechanicalsWheels,
  useInspectionsVehiclesMechanicalsNotes,
  useUpdateInspectionMechanicalInspection,
} from '@/api/Inspections';
import {
  DynamicOperations,
  ElectricHybrid,
  EngineRunning,
  Inspection,
  Interior,
  MechanicalsWeels,
  ReturnVehicleCheckListValue,
  UpdateMechanicalInspection,
  Wheel,
  WheelDetails,
} from '@/types';
import { formatChecklistLabel, formatChecklistValue } from './helpers';

type WheelGroup = 'frontLeft' | 'frontRight' | 'rearLeft' | 'rearRight';

const buildWheel = (
  inspection: Inspection,
  group: WheelGroup,
  wheelsEntity?: MechanicalsWeels,
): Wheel => ({
  tyreBrand:
    (inspection[`${group}TyreBrand` as keyof Inspection] as string) || '',
  wheelType:
    (inspection[`${group}WheelType` as keyof Inspection] as string) || '',
  tyreWidth:
    (inspection[`${group}TyreWidth` as keyof Inspection] as number) || 0,
  tyreHeight:
    (inspection[`${group}TyreHeight` as keyof Inspection] as number) || 0,
  rimRadius:
    (inspection[`${group}RimRadius` as keyof Inspection] as number) || 0,
  tyreLoad: (inspection[`${group}TyreLoad` as keyof Inspection] as number) || 0,
  tyreSpeed:
    (inspection[`${group}TyreSpeed` as keyof Inspection] as string) || '',
  wheelInnerDepth:
    (wheelsEntity?.[
      `${group}WheelInnerDepth` as keyof MechanicalsWeels
    ] as string) || '',
  wheelMiddleDepth:
    (wheelsEntity?.[
      `${group}WheelMiddleDepth` as keyof MechanicalsWeels
    ] as string) || '',
  wheelOuterDepth:
    (wheelsEntity?.[
      `${group}WheelOuterDepth` as keyof MechanicalsWeels
    ] as string) || '',
});

const buildSpareWheel = (inspection: Inspection): Wheel => ({
  tyreBrand: inspection.spareTyreBrand || '',
  wheelType: inspection.spareWheelType || '',
  tyreWidth: inspection.spareTyreWidth || 0,
  tyreHeight: inspection.spareTyreHeight || 0,
  rimRadius: inspection.spareRimRadius || 0,
  tyreLoad: inspection.spareTyreLoad || 0,
  tyreSpeed: inspection.spareTyreSpeed || '',
  wheelInnerDepth: inspection.spareWheelDepth || '',
  wheelMiddleDepth: inspection.spareWheelDepth || '',
  wheelOuterDepth: inspection.spareWheelDepth || '',
});

const buildWheelDetails = (
  inspection: Inspection,
  wheelsEntity?: MechanicalsWeels,
): WheelDetails => ({
  frontLeftWheel: buildWheel(inspection, 'frontLeft', wheelsEntity),
  frontRightWheel: buildWheel(inspection, 'frontRight', wheelsEntity),
  rearLeftWheel: buildWheel(inspection, 'rearLeft', wheelsEntity),
  rearRightWheel: buildWheel(inspection, 'rearRight', wheelsEntity),
  spareWheel: buildSpareWheel(inspection),
});

const omitId = <T extends { id?: number }>(value?: T): Omit<T, 'id'> => {
  const rest: T = { ...(value || ({} as T)) };
  delete (rest as { id?: number }).id;
  return rest;
};

type ChecklistSectionKey =
  | 'dynamicOperations'
  | 'engineRunning'
  | 'electricHybrid'
  | 'interior';

type ChecklistFieldType = 'checkValue' | 'yesNo' | 'number' | 'text';

type ChecklistFieldConfig = {
  name: string;
  type: ChecklistFieldType;
};

const DYNAMIC_OPERATIONS_FIELDS: ChecklistFieldConfig[] = [
  { name: 'carryOutIstaVehicleTest', type: 'checkValue' },
  { name: 'deletePersonalData', type: 'checkValue' },
  { name: 'readKeyPositions', type: 'checkValue' },
];

const ENGINE_RUNNING_FIELDS: ChecklistFieldConfig[] = [
  { name: 'engineRunningSmokingNoise', type: 'checkValue' },
  { name: 'exhaustNoiseSecure', type: 'checkValue' },
  { name: 'checkExteriorLights', type: 'checkValue' },
  { name: 'suspensionVehicleLevel', type: 'checkValue' },
  { name: 'checkBrakeDiscsPads', type: 'checkValue' },
  { name: 'checkOilLevel', type: 'checkValue' },
  { name: 'checkBrakeFluidLevel', type: 'checkValue' },
  { name: 'checkCoolantLevel', type: 'checkValue' },
  { name: 'shortDrive', type: 'checkValue' },
];

const ELECTRIC_HYBRID_FIELDS: ChecklistFieldConfig[] = [
  { name: 'chargePortCondition', type: 'checkValue' },
  { name: 'coolantLevel', type: 'checkValue' },
  { name: 'modeThreeCablePresent', type: 'checkValue' },
  { name: 'modeTwoCablePresent', type: 'checkValue' },
  { name: 'vehicleAcceptsCharge', type: 'checkValue' },
  { name: 'vehicleChargeLevel', type: 'checkValue' },
];

const INTERIOR_FIELDS: ChecklistFieldConfig[] = [
  { name: 'numberOfKeys', type: 'number' },
  { name: 'fuelLevel', type: 'number' },
  { name: 'motDate', type: 'text' },
  { name: 'isV5Present', type: 'yesNo' },
  { name: 'breakFunctionTest', type: 'checkValue' },
  { name: 'handBrakeTest', type: 'checkValue' },
  { name: 'steeringNoise', type: 'checkValue' },
  { name: 'staticGearSelection', type: 'checkValue' },
  { name: 'checkCorrectTimeAndDateSet', type: 'checkValue' },
  { name: 'checkEngineManagementLight', type: 'checkValue' },
  { name: 'checkAudioDisplaySystems', type: 'checkValue' },
  { name: 'checkDscWarningLight', type: 'checkValue' },
  { name: 'batteryNoErrorMessage', type: 'checkValue' },
  { name: 'checkAirBagWarningLight', type: 'checkValue' },
  { name: 'checkAllWindow', type: 'checkValue' },
  { name: 'checkSunroofConvertibleRoof', type: 'checkValue' },
  { name: 'checkCentralLocking', type: 'checkValue' },
  { name: 'checkHandbook', type: 'checkValue' },
  { name: 'checkAirbagStatus', type: 'checkValue' },
  { name: 'checkAirConditionFunctionality', type: 'checkValue' },
  { name: 'checkWipersAndWashes', type: 'checkValue' },
  { name: 'checkHorn', type: 'checkValue' },
  { name: 'checkSeatbeltFunction', type: 'checkValue' },
  { name: 'checkNavFunctionality', type: 'checkValue' },
];

const SECTIONS: { key: ChecklistSectionKey; fields: ChecklistFieldConfig[] }[] =
  [
    { key: 'dynamicOperations', fields: DYNAMIC_OPERATIONS_FIELDS },
    { key: 'engineRunning', fields: ENGINE_RUNNING_FIELDS },
    { key: 'electricHybrid', fields: ELECTRIC_HYBRID_FIELDS },
    { key: 'interior', fields: INTERIOR_FIELDS },
  ];

const CHECK_VALUE_OPTIONS = Object.values(ReturnVehicleCheckListValue).map(
  (value) => ({
    value,
    label: formatChecklistValue(value),
  }),
);

const YES_NO_OPTIONS = [
  { value: 'Yes', label: 'Yes' },
  { value: 'No', label: 'No' },
];

type MechanicalInspectionFormValues = {
  dynamicOperations: DynamicOperations;
  engineRunning: EngineRunning;
  electricHybrid: ElectricHybrid;
  interior: Interior;
};

interface UpdateMechanicalInspectionPopupProps {
  inspection: Inspection;
  open: boolean;
  onClose: () => void;
  dynamicOperations?: DynamicOperations;
  engineRunning?: EngineRunning;
  electricHybrid?: ElectricHybrid;
  interior?: Interior;
}

export const UpdateMechanicalInspectionPopup: FC<
  UpdateMechanicalInspectionPopupProps
> = ({
  inspection,
  open,
  onClose,
  dynamicOperations,
  engineRunning,
  electricHybrid,
  interior,
}) => {
  const { t } = useTranslate();
  const [form] = Form.useForm<MechanicalInspectionFormValues>();
  const [error, setError] = useState<string>();

  const { mutateAsync: updateMechanicalInspection, isPending } =
    useUpdateInspectionMechanicalInspection();
  const { data: wheelsData } = useInspectionsMechanicalsWheels(inspection.id);
  const { data: mechanicalsNotesData } = useInspectionsVehiclesMechanicalsNotes(
    inspection.id,
  );

  const wheelsEntity = wheelsData?.entities?.[0];
  const comments = mechanicalsNotesData?.entities?.[0]?.notes || '';

  useEffect(() => {
    if (!open) return;
    setError(undefined);
    form.setFieldsValue({
      dynamicOperations: dynamicOperations || ({} as DynamicOperations),
      engineRunning: engineRunning || ({} as EngineRunning),
      electricHybrid: electricHybrid || ({} as ElectricHybrid),
      interior: interior || ({} as Interior),
    });
  }, [open, dynamicOperations, engineRunning, electricHybrid, interior, form]);

  const handleSubmit = async (values: MechanicalInspectionFormValues) => {
    setError(undefined);

    const payload: UpdateMechanicalInspection = {
      id: inspection.id,
      vin: inspection.vin,
      mechanicalInspectionInformation: {
        interiorCheckList: omitId({ ...interior, ...values.interior }),
        electricCheckList: omitId({
          ...electricHybrid,
          ...values.electricHybrid,
        }),
        runningEngineBayCheckList: omitId({
          ...engineRunning,
          ...values.engineRunning,
        }),
        istaCheckList: omitId({
          ...dynamicOperations,
          ...values.dynamicOperations,
        }),
        wheelDetails: buildWheelDetails(inspection, wheelsEntity),
        summary: { comments },
      },
    };

    try {
      const res = await updateMechanicalInspection(payload);
      if (!res?.success) {
        setError(res?.message?.Message || t('generalError'));
        return;
      }
      onClose();
    } catch (err) {
      console.log('handleUpdateMechanicalInspection: ', { error: err });
      setError(t('generalError'));
    }
  };

  const renderField = (
    sectionKey: ChecklistSectionKey,
    field: ChecklistFieldConfig,
  ) => {
    const label = formatChecklistLabel(field.name);
    const name = [sectionKey, field.name];

    if (field.type === 'number') {
      return (
        <Form.Item key={field.name} name={name} label={label}>
          <InputNumber className="w-100" min={0} />
        </Form.Item>
      );
    }

    if (field.type === 'text') {
      return (
        <Form.Item key={field.name} name={name} label={label}>
          <Input />
        </Form.Item>
      );
    }

    if (field.type === 'yesNo') {
      return (
        <Form.Item key={field.name} name={name} label={label}>
          <Select options={YES_NO_OPTIONS} allowClear />
        </Form.Item>
      );
    }

    return (
      <Form.Item key={field.name} name={name} label={label}>
        <Select options={CHECK_VALUE_OPTIONS} allowClear />
      </Form.Item>
    );
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={t('updateMechanicalInspection')}
      width={760}
      centered
      okText={t('save')}
      cancelText={t('cancel')}
      onOk={() => form.submit()}
      confirmLoading={isPending}
      destroyOnHidden
    >
      <Spin spinning={isPending}>
        <Form
          id="mechanical-inspection-form"
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Collapse
            defaultActiveKey={SECTIONS.map(({ key }) => key)}
            items={SECTIONS.map(({ key, fields }) => ({
              key,
              label: t(key as TranslationKey),
              children: (
                <Row gutter={16}>
                  {fields.map((field) => (
                    <Col xs={24} md={12} key={field.name}>
                      {renderField(key, field)}
                    </Col>
                  ))}
                </Row>
              ),
            }))}
          />
          {error ? (
            <Alert message={error} type="error" className="mt-3" />
          ) : null}
        </Form>
      </Spin>
    </Modal>
  );
};
